import { useCallback, useMemo, useState } from 'react';
import {
  useGetNotificationsQuery,
  useDeleteNotificationMutation,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
} from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/NotificationsRpc.hooks';
import { GET_NOTIFICATIONS_QUERY } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/NotificationsRpc.dfc.new';
import type { PortalNotificationResponse } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/NotificationsRpc';
import { getPortalAccountId, getPortalInstanceId } from '../utils/metadata';
import type {
  NotificationType,
  PortalNotification,
} from '../types/notifications';

const BADGE_LIMIT = 1;

interface UseNotificationsOptions {
  limit: number;
}

function toNotificationType(rpcType: string): NotificationType | null {
  switch (rpcType) {
    case 'PAYMENT_FAILED':
    case 'INVOICE_UNPAID':
    case 'SUBSCRIPTION_CANCELLATION_REQUESTED':
      return 'BILLING';
    case 'DOCUMENT_SHARED':
    case 'ACCESS_GRANTED':
    case 'PORTAL_ACTIVITY':
      return 'DEALS';
    default:
      return null;
  }
}

function toPortalNotification(
  rpc: PortalNotificationResponse
): PortalNotification | null {
  const notificationType = toNotificationType(String(rpc.notificationType));
  if (!notificationType) return null;
  return {
    id: rpc.id,
    portalInstanceId: rpc.portalInstanceId,
    notificationType,
    title: rpc.title,
    body: rpc.body,
    ctaLink: rpc.ctaLink ?? null,
    createdAt: rpc.createdAt,
    isRead: rpc.isRead,
  };
}

export function useNotifications({ limit }: UseNotificationsOptions) {
  const [loadingMore, setLoadingMore] = useState(false);

  const [deleteNotification] = useDeleteNotificationMutation();
  const [markNotificationAsRead] = useMarkNotificationAsReadMutation();
  const [markAllNotificationsAsRead] = useMarkAllNotificationsAsReadMutation();

  const { data, loading, error, fetchMore } = useGetNotificationsQuery({
    variables: {
      offset: 0,
      limit,
      accountId: getPortalAccountId(),
      portalInstanceId: getPortalInstanceId(),
    },
    fetchPolicy: 'cache-and-network',
  });

  const totalCount = data?.totalCount ?? 0;
  const unreadCount = data?.unreadCount ?? 0;

  const notifications = useMemo(
    () =>
      (data?.notifications ?? [])
        .map(toPortalNotification)
        .filter((n): n is PortalNotification => n !== null),
    [data?.notifications]
  );

  const remove = useCallback(
    (notificationId: number) => {
      return deleteNotification({
        variables: {
          notificationId,
          accountId: getPortalAccountId(),
          portalInstanceId: getPortalInstanceId(),
        },
        optimisticResponse: { result: {} },
        update(cache) {
          let wasUnread = false;
          const queryVars = (l: number) => ({
            query: GET_NOTIFICATIONS_QUERY,
            variables: {
              offset: 0,
              limit: l,
              accountId: getPortalAccountId(),
              portalInstanceId: getPortalInstanceId(),
            },
          });
          cache.updateQuery(queryVars(limit), (existing) => {
            if (!existing?.result) return existing;
            wasUnread = existing.result.notifications.some(
              (n: PortalNotificationResponse) =>
                n.id === notificationId && !n.isRead
            );
            return {
              result: {
                ...existing.result,
                notifications: existing.result.notifications.filter(
                  (n: PortalNotificationResponse) => n.id !== notificationId
                ),
                totalCount: existing.result.totalCount - 1,
                unreadCount: wasUnread
                  ? existing.result.unreadCount - 1
                  : existing.result.unreadCount,
              },
            };
          });
          cache.updateQuery(queryVars(BADGE_LIMIT), (existing) => {
            if (!existing?.result || !wasUnread) return existing;
            return {
              result: {
                ...existing.result,
                unreadCount: existing.result.unreadCount - 1,
              },
            };
          });
        },
      });
    },
    [deleteNotification, limit]
  );

  const markAsRead = useCallback(
    (notificationId: number) => {
      return markNotificationAsRead({
        variables: {
          notificationId,
          accountId: getPortalAccountId(),
          portalInstanceId: getPortalInstanceId(),
        },
        optimisticResponse: { result: {} },
        update(cache) {
          let wasUnread = false;
          const queryVars = (l: number) => ({
            query: GET_NOTIFICATIONS_QUERY,
            variables: {
              offset: 0,
              limit: l,
              accountId: getPortalAccountId(),
              portalInstanceId: getPortalInstanceId(),
            },
          });
          cache.updateQuery(queryVars(limit), (existing) => {
            if (!existing?.result) return existing;
            wasUnread = existing.result.notifications.some(
              (n: PortalNotificationResponse) =>
                n.id === notificationId && !n.isRead
            );
            return {
              result: {
                ...existing.result,
                notifications: existing.result.notifications.map(
                  (n: PortalNotificationResponse) =>
                    n.id === notificationId ? { ...n, isRead: true } : n
                ),
                unreadCount: wasUnread
                  ? existing.result.unreadCount - 1
                  : existing.result.unreadCount,
              },
            };
          });
          cache.updateQuery(queryVars(BADGE_LIMIT), (existing) => {
            if (!existing?.result || !wasUnread) return existing;
            return {
              result: {
                ...existing.result,
                unreadCount: existing.result.unreadCount - 1,
              },
            };
          });
        },
      });
    },
    [markNotificationAsRead, limit]
  );

  const markAllAsRead = useCallback(() => {
    return markAllNotificationsAsRead({
      variables: {
        accountId: getPortalAccountId(),
        portalInstanceId: getPortalInstanceId(),
      },
      optimisticResponse: { result: { updatedCount: unreadCount } },
      update(cache) {
        const queryVars = (l: number) => ({
          query: GET_NOTIFICATIONS_QUERY,
          variables: {
            offset: 0,
            limit: l,
            accountId: getPortalAccountId(),
            portalInstanceId: getPortalInstanceId(),
          },
        });
        cache.updateQuery(queryVars(limit), (existing) => {
          if (!existing?.result) return existing;
          return {
            result: {
              ...existing.result,
              notifications: existing.result.notifications.map(
                (n: PortalNotificationResponse) => ({ ...n, isRead: true })
              ),
              unreadCount: 0,
            },
          };
        });
        cache.updateQuery(queryVars(BADGE_LIMIT), (existing) => {
          if (!existing?.result) return existing;
          return {
            result: { ...existing.result, unreadCount: 0 },
          };
        });
      },
    });
  }, [markAllNotificationsAsRead, unreadCount, limit]);

  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    fetchMore({
      variables: { offset: notifications.length },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          result: {
            ...fetchMoreResult.result,
            notifications: [
              ...(prev.result.notifications ?? []),
              ...fetchMoreResult.result.notifications,
            ],
          },
        };
      },
    })
      .catch(console.error)
      .finally(() => setLoadingMore(false));
  }, [loadingMore, fetchMore, notifications.length]);

  return {
    notifications,
    unreadCount,
    totalCount,
    loading,
    loadingMore,
    hasMore: notifications.length < totalCount,
    error: !!error,
    loadMore,
    markAsRead,
    markAllAsRead,
    remove,
  };
}
